<template>
  <a-modal
    title="发货"
    :visible="visible"
    @ok="handleSubmit"
    @cancel="handleCancel"
    width="1300px"
    :confirm-loading="loading"
  >
    <a-form-model :model="form" :label-col="{ span: 4 }" :wrapper-col="{ span: 18 }">
      <a-form-model-item label="要货单号">
        <span>{{ orderData.orderNo }}</span>
      </a-form-model-item>
      <a-form-model-item label="门店名称">
        <span>{{ orderData.storeName }}</span>
      </a-form-model-item>
    </a-form-model>

    <a-alert
      v-if="stockWarning"
      message="库存不足警告"
      :description="stockWarningMessage"
      type="warning"
      show-icon
      style="margin-bottom: 16px"
    />

    <a-divider>商品明细</a-divider>

    <a-table
      rowKey="id"
      :columns="columns"
      :data-source="orderData.items || []"
      :pagination="false"
      size="small"
      :scroll="{ x: 1250 }"
    >
      <span slot="shipEnabled" slot-scope="text, record">
        <a-checkbox
          v-if="isShippable(record)"
          v-model="record.shipEnabled"
          @change="calculateTotal"
        >发货</a-checkbox>
        <span v-else>-</span>
      </span>

      <span slot="itemStatus" slot-scope="text">
        <a-tag :color="getItemStatusColor(text)">{{ getItemStatusText(text) }}</a-tag>
      </span>

      <span slot="deliveryType" slot-scope="text, record">
        <a-select
          v-if="isShippable(record)"
          v-model="record.deliveryType"
          size="small"
          :disabled="!record.shipEnabled"
          style="width: 130px"
          @change="e => handleDeliveryTypeChange(record, e)"
        >
          <a-select-option :value="1">直接发货</a-select-option>
          <a-select-option :value="2">直发采购</a-select-option>
        </a-select>
        <span v-else>-</span>
      </span>

      <span slot="supplier" slot-scope="text, record">
        <a-select
          v-if="isShippable(record) && record.deliveryType === 2"
          v-model="record.supplierId"
          size="small"
          show-search
          :filter-option="filterOption"
          :disabled="!record.shipEnabled"
          placeholder="请选择供应商"
          style="width: 180px"
        >
          <a-select-option
            v-for="supplier in supplierList"
            :key="supplier.id"
            :value="supplier.id"
          >
            {{ supplier.supplierName }}
          </a-select-option>
        </a-select>
        <span v-else>-</span>
      </span>

      <span slot="shipQty" slot-scope="text, record">
        <a-input-number
          v-if="isShippable(record) && record.shipEnabled"
          v-model="record.shipQty"
          :min="0"
          :max="record.maxShipQty || 0"
          :precision="0"
          style="width: 100px"
          size="small"
          @change="calculateTotal"
        />
        <span v-else>-</span>
      </span>
    </a-table>

    <div style="margin-top: 16px; text-align: right;">
      <a-statistic
        title="本次发货总数"
        :value="totalShipCount"
        suffix="件"
      />
    </div>
  </a-modal>
</template>

<script>
import { getSupplierList } from '@/api/retail/inventory'

// 要货明细行状态：0待审核 1已审核 2已发货 3已取消 4已收货 5审核驳回
const ITEM_STATUS_APPROVED = 1
// 行发货方式：1直接发货(平台库存) 2直发采购(供应商直发)
const DELIVERY_TYPE_DIRECT = 1
const DELIVERY_TYPE_PURCHASE = 2

// 仓库管理角色标识：该角色仅可查看商品名称与数量，不可查看任何金额
const WAREHOUSE_ROLE_KEYS = ['仓库管理']

export default {
  name: 'ShipModal',
  data() {
    return {
      visible: false,
      loading: false,
      orderData: {},
      form: {
        remark: ''
      },
      supplierList: [], // 供应商列表
      totalShipCount: 0,
      stockWarning: false,
      stockWarningMessage: ''
    }
  },
  computed: {
    // 当前登录用户是否为仓库管理角色
    isWarehouseManager() {
      const roles = this.$store.getters.roles || []
      const roleList = Array.isArray(roles) ? roles : Array.from(roles)
      return WAREHOUSE_ROLE_KEYS.some(key => roleList.includes(key))
    },
    // 商品明细列：仓库管理角色只展示商品名称与数量，隐藏所有金额列
    columns() {
      const goodsColumns = [
        { title: '商品名称', dataIndex: 'goodsName', width: 140, align: 'center', ellipsis: true },
        { title: 'SKU名称', dataIndex: 'skuName', width: 110, align: 'center', ellipsis: true }
      ]
      const countColumns = [
        { title: '本次发货', dataIndex: 'shipEnabled', scopedSlots: { customRender: 'shipEnabled' }, width: 90, align: 'center' },
        { title: '要货数量', dataIndex: 'applyCount', width: 80, align: 'center' },
        { title: '状态', dataIndex: 'itemStatus', scopedSlots: { customRender: 'itemStatus' }, width: 90, align: 'center' },
        { title: '发货方式', dataIndex: 'deliveryType', scopedSlots: { customRender: 'deliveryType' }, width: 140, align: 'center' },
        { title: '供应商', dataIndex: 'supplierId', scopedSlots: { customRender: 'supplier' }, width: 190, align: 'center' },
        { title: '本次发货数量', dataIndex: 'shipQty', scopedSlots: { customRender: 'shipQty' }, width: 120, align: 'center' }
      ]
      if (this.isWarehouseManager) {
        return [...goodsColumns, ...countColumns]
      }
      return [
        ...goodsColumns,
        { title: '要货数量', dataIndex: 'applyCount', width: 80, align: 'center' },
        { title: '库存', dataIndex: 'stockCount', width: 80, align: 'center' },
        { title: '采购价(元)', dataIndex: 'platformPrice', width: 90, align: 'center', customRender: (text) => `¥${text ? parseFloat(text).toFixed(2) : '0.00'}` },
        { title: '商家拿货价(元)', dataIndex: 'merchantPrice', width: 100, align: 'center', customRender: (text) => `¥${text ? parseFloat(text).toFixed(2) : '0.00'}` },
        countColumns[0],
        countColumns[2],
        countColumns[3],
        countColumns[4],
        countColumns[5]
      ]
    }
  },
  mounted() {
    this.loadSupplierList()
  },
  methods: {
    // 加载供应商列表
    async loadSupplierList() {
      try {
        const res = await getSupplierList()
        // 响应 code 是字符串 '20001'，此处直接取 data（拦截器已保证请求成功）
        this.supplierList = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        console.error('加载供应商列表失败:', error)
        this.supplierList = []
      }
    },

    // 下拉框搜索过滤
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },

    getItemStatusText(status) {
      const map = { 0: '待审核', 1: '已审核', 2: '已发货', 3: '已取消', 4: '已收货', 5: '审核驳回' }
      return map[status] || '-'
    },
    getItemStatusColor(status) {
      const map = { 0: 'orange', 1: 'blue', 2: 'cyan', 3: 'red', 4: 'green', 5: 'purple' }
      return map[status] || 'default'
    },

    // 只有审核通过的商品才能发货
    isShippable(record) {
      return record.itemStatus === ITEM_STATUS_APPROVED
    },

    /**
     * 打开发货弹窗
     * @param orderData 要货单详情（含 items）
     */
    open(orderData) {
      this.visible = true
      this.loading = false
      this.orderData = orderData
      this.form.remark = ''
      this.stockWarning = false
      this.stockWarningMessage = ''

      const insufficientNames = []
      ;(this.orderData.items || []).forEach(item => {
        // 本次可发货数量 = 审批数量 - 已发货数量
        const approveCount = item.approveCount != null ? item.approveCount : (item.applyCount || 0)
        const shipped = item.shipCount || 0
        const remain = Math.max((approveCount || 0) - shipped, 0)
        item.maxShipQty = remain

        if (!this.isShippable(item)) {
          item.shipEnabled = false
          item.shipQty = 0
          return
        }
        // 默认本次发货，可取消勾选表示该商品本次不发
        item.shipEnabled = true
        // 行发货方式：默认直接发货，库存不足时由用户改选直发采购
        item.deliveryType = item.deliveryType || DELIVERY_TYPE_DIRECT
        item.shipQty = remain
        if (!item.inStock) {
          insufficientNames.push(`${item.goodsName}(${item.skuName})`)
        }
      })

      if (insufficientNames.length > 0) {
        this.stockWarning = true
        this.stockWarningMessage = `以下商品库存不足,建议使用"直发采购"方式: ${insufficientNames.join('、')}`
      }

      this.calculateTotal()
    },

    // 切换行发货方式
    handleDeliveryTypeChange(record, e) {
      const deliveryType = e.target.value
      if (deliveryType === DELIVERY_TYPE_PURCHASE && record.shipEnabled && !record.supplierId) {
        this.$message.warning(`商品[${record.goodsName}]选择直发采购后需选择供应商`)
      }
      this.calculateTotal()
    },

    calculateTotal() {
      this.totalShipCount = (this.orderData.items || []).reduce((total, item) => {
        if (!this.isShippable(item) || !item.shipEnabled) {
          return total
        }
        return total + (item.shipQty || 0)
      }, 0)
    },

    handleCancel() {
      this.visible = false
      this.loading = false
    },

    handleSubmit() {
      // 待发货明细中勾选了"本次发货"的行
      const selectedItems = (this.orderData.items || []).filter(item => {
        return this.isShippable(item) && item.shipEnabled
      })

      if (selectedItems.length === 0) {
        this.$message.error('请至少勾选一个要发货的商品')
        return
      }

      // 直发采购必须选择供应商
      const missingSupplier = selectedItems.find(item => {
        return item.deliveryType === DELIVERY_TYPE_PURCHASE && (item.shipQty || 0) > 0 && !item.supplierId
      })
      if (missingSupplier) {
        this.$message.error(`商品[${missingSupplier.goodsName}]选择直发采购后需选择供应商`)
        return
      }

      const shipItems = selectedItems
        .filter(item => (item.shipQty || 0) > 0)
        .map(item => ({
          itemId: item.id,
          goodsId: item.goodsId,
          skuId: item.skuId,
          shipCount: item.deliveryType === DELIVERY_TYPE_PURCHASE ? 0 : (item.shipQty || 0),
          purchaseCount: item.deliveryType === DELIVERY_TYPE_PURCHASE ? (item.shipQty || 0) : 0,
          deliveryType: item.deliveryType,
          supplierId: item.deliveryType === DELIVERY_TYPE_PURCHASE ? item.supplierId : undefined
        }))

      if (shipItems.length === 0) {
        this.$message.error('请至少设置一个商品的发货数量')
        return
      }

      this.loading = true

      // 调用父组件的发货方法
      this.$emit('submit', {
        orderId: this.orderData.id,
        items: shipItems,
        remark: this.form.remark
      })
    }
  }
}
</script>

<style lang="less" scoped>
</style>
